import { createStyles, Typography, withStyles, WithStyles } from '@material-ui/core';
import React, { useState } from 'react';

import { AvailableHourRange } from '../../models/Sessions';
import { colors } from '../../themes/colors';
import AvailableHourInput from './AvailableHourRangeInput';

const styles = () =>
  createStyles({
    day: {
      display: 'flex',
      flexDirection: 'row',
      padding: 10,
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    dayLabel: {
      textTransform: 'capitalize',
      fontSize: 16,
      fontWeight: 900,
      width: 150,
    },
    hours: {
      flexGrow: 1,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    flowBuffer: {
      flex: 1,
    },
    error: {
      color: colors.red2,
    },
  });

enum AvailableDayInputTestID {
  ContainerPrefix = 'AvailableDayInputTestID.ContainerPrefix',
}

interface Props extends WithStyles<typeof styles> {
  dayName: string;
  hours: AvailableHourRange[];
  onHoursUpdated: (day: string, newHours: AvailableHourRange[]) => void;
  isTimeRangeValid: (start?: string, end?: string) => boolean;
}

const AvailableDayInput: React.FC<Props> = ({
  dayName,
  hours,
  onHoursUpdated,
  classes,
  isTimeRangeValid,
}) => {
  const [dayHours, setDayHours] = useState<AvailableHourRange[]>([...hours, { day: dayName }]);
  const [errors, setErrors] = useState({});

  const addNewHourRange = () => {
    setDayHours([...dayHours, { day: dayName }]);
  };

  const deleteHourRange = (index: number) => {
    let newHours = [{ day: dayName }];
    if (dayHours.length !== 1) {
      newHours = dayHours.slice(0, index).concat(dayHours.slice(index + 1));
    }

    setDayHours(newHours);
    onHoursUpdated(dayName, newHours);
  };

  const updateHours = (index: number, start: string, end: string) => {
    const newHour = { day: dayName, start, end };
    // List of existing hours with the old hour replaced by the new one
    const newHours = dayHours.map((p, i) => (i === index ? newHour : p));

    // Check that the provided values are valid.
    const timeRangeValid = isTimeRangeValid(start, end);

    setErrors({
      ...errors,
      [index]: timeRangeValid ? null : 'End time has to be later than start time.',
    });

    setDayHours(newHours);
    onHoursUpdated(dayName, newHours);
  };

  return (
    <div
      className={classes.day}
      data-testid={`${AvailableDayInputTestID.ContainerPrefix}${dayName}`}
    >
      <Typography className={classes.dayLabel}>{dayName}</Typography>
      <div className={classes.hours}>
        {dayHours.map((hour, index) => (
          <React.Fragment key={`${hour.start}${hour.end}${index}`}>
            <AvailableHourInput
              hour={hour}
              addNewHourRange={addNewHourRange}
              deleteHourRange={() => deleteHourRange(index)}
              showAddButton={index === dayHours.length - 1}
              onRangeUpdated={(start, end) => updateHours(index, start, end)}
              hasError={errors && errors[index]}
              testIDPostfix={`${dayName}-${index}`}
            />
            {errors && errors[index] && <span className={classes.error}>{errors[index]}</span>}
          </React.Fragment>
        ))}
      </div>
      <div className={classes.flowBuffer} />
    </div>
  );
};

export default withStyles(styles)(AvailableDayInput);
